import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { memo, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "./arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { Flex } from "./flex";
import * as styles from "./json-value-editor.module.css";
import { Text } from "./text";

/** Resolve an object property's JSON Schema through union wrappers. */
export function jsonSchemaProperty(schema: unknown, key: string): unknown {
  if (!schema || typeof schema !== "object") {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const node = schema as Record<string, unknown>;
  const properties = node.properties;
  if (
    properties &&
    typeof properties === "object" &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    key in (properties as Record<string, unknown>)
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return (properties as Record<string, unknown>)[key];
  }
  if (
    node.additionalProperties &&
    typeof node.additionalProperties === "object"
  ) {
    return node.additionalProperties;
  }
  for (const branchKey of ["anyOf", "oneOf"]) {
    const branches = node[branchKey];
    if (Array.isArray(branches)) {
      for (const branch of branches) {
        const found = jsonSchemaProperty(branch, key);
        if (found !== undefined) {
          return found;
        }
      }
    }
  }
  return undefined;
}

function jsonSchemaItems(schema: unknown): unknown {
  if (!schema || typeof schema !== "object") {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const node = schema as Record<string, unknown>;
  if (node.items && typeof node.items === "object") {
    return node.items;
  }
  for (const branchKey of ["anyOf", "oneOf"]) {
    const branches = node[branchKey];
    if (Array.isArray(branches)) {
      for (const branch of branches) {
        const found = jsonSchemaItems(branch);
        if (found !== undefined) {
          return found;
        }
      }
    }
  }
  return undefined;
}

export function jsonValueMatchesSearch(
  value: unknown,
  searchString: string,
): boolean {
  const query = searchString.trim().toLocaleLowerCase();
  if (!query) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some((item) => jsonValueMatchesSearch(item, query));
  }
  if (typeof value === "object" && value !== null) {
    return Object.entries(value).some(
      ([key, child]) =>
        key.toLocaleLowerCase().includes(query) ||
        jsonValueMatchesSearch(child, query),
    );
  }
  return String(value).toLocaleLowerCase().includes(query);
}

type PrimitiveTypeName = "string" | "number" | "boolean" | "null";

/** Collect the primitive types admitted by a JSON Schema node. */
function allowedPrimitiveTypes(schema: unknown): Set<PrimitiveTypeName> {
  const allowed = new Set<PrimitiveTypeName>();
  collect(schema);
  return allowed;

  function addTypeName(name: unknown) {
    if (name === "integer") {
      allowed.add("number");
    } else if (
      name === "string" ||
      name === "number" ||
      name === "boolean" ||
      name === "null"
    ) {
      allowed.add(name);
    }
  }

  function collect(value: unknown) {
    if (!value || typeof value !== "object") {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const node = value as Record<string, unknown>;
    if (Array.isArray(node.type)) {
      for (const typeName of node.type) {
        addTypeName(typeName);
      }
    } else {
      addTypeName(node.type);
    }
    for (const branchKey of ["anyOf", "oneOf", "allOf"]) {
      const branches = node[branchKey];
      if (Array.isArray(branches)) {
        for (const branch of branches) {
          collect(branch);
        }
      }
    }
    if (Array.isArray(node.enum)) {
      for (const member of node.enum) {
        addTypeName(member === null ? "null" : typeof member);
      }
    }
    if ("const" in node) {
      addTypeName(node.const === null ? "null" : typeof node.const);
    }
  }
}

/** Recursive, structurally preserving editor for arbitrary JSON values. */
export const JsonValueEditor = memo(function JsonValueEditor({
  label,
  value,
  setValue,
  schema,
  defaultCollapsed = true,
  highlighted = false,
  readonly,
  searchString = "",
  searchLabel = true,
}: {
  label: string;
  value: unknown;
  setValue: (value: unknown) => void;
  /** JSON Schema node describing this value, when known. */
  schema?: unknown;
  defaultCollapsed?: boolean;
  highlighted?: boolean;
  readonly?: boolean;
  searchString?: string;
  searchLabel?: boolean;
}) {
  const normalizedSearchString = searchString.trim();
  const query = normalizedSearchString.toLocaleLowerCase();
  const labelMatches =
    searchLabel && query ? label.toLocaleLowerCase().includes(query) : false;
  const childSearchString = labelMatches ? "" : normalizedSearchString;
  const shouldHighlight =
    highlighted ||
    labelMatches ||
    Boolean(
      query &&
      typeof value !== "object" &&
      String(value).toLocaleLowerCase().includes(query),
    );
  if (Array.isArray(value)) {
    return (
      <ArrayEditor
        defaultCollapsed={defaultCollapsed}
        highlighted={shouldHighlight}
        label={label}
        readonly={readonly}
        schema={schema}
        searchString={childSearchString}
        setValue={setValue}
        value={value}
      />
    );
  }
  if (typeof value === "object" && value !== null) {
    return (
      <ObjectEditor
        defaultCollapsed={defaultCollapsed}
        highlighted={shouldHighlight}
        label={label}
        readonly={readonly}
        schema={schema}
        searchString={childSearchString}
        setValue={setValue}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        value={value as Record<string, unknown>}
      />
    );
  }
  return (
    <PrimitiveRow
      highlighted={shouldHighlight}
      label={label}
      readonly={readonly}
      schema={schema}
      setValue={setValue}
      value={value}
    />
  );
});

const ObjectEditor = memo(function ObjectEditor({
  label,
  value,
  setValue,
  schema,
  defaultCollapsed,
  highlighted,
  readonly,
  searchString,
}: {
  label: string;
  value: Record<string, unknown>;
  setValue: (value: unknown) => void;
  schema?: unknown;
  defaultCollapsed: boolean;
  highlighted: boolean;
  readonly?: boolean;
  searchString: string;
}) {
  const entries = Object.entries(value);
  const visibleEntries = searchString
    ? entries.filter(
        ([key, fieldValue]) =>
          key.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          jsonValueMatchesSearch(fieldValue, searchString),
      )
    : entries;
  return (
    <Flex
      bgColor={highlighted ? "warning-secondary" : undefined}
      borderRadius={highlighted ? "sm" : undefined}
      data-json-search-match={highlighted ? "true" : undefined}
      dir="column"
    >
      <RedoCollapseSubsection
        defaultCollapsed={
          searchString || highlighted ? false : defaultCollapsed
        }
        key={searchString || highlighted ? "searching" : "default"}
        title={`${label} {${visibleEntries.length}}`}
      >
        <Flex dir="column" gap="xs" pl="xl" pt="xs">
          {visibleEntries.length === 0 ? (
            <Text fontSize="xs" textColor="secondary">
              No fields
            </Text>
          ) : (
            visibleEntries.map(([key, fieldValue]) => (
              <JsonValueEditor
                key={key}
                label={key}
                readonly={readonly}
                schema={jsonSchemaProperty(schema, key)}
                searchString={searchString}
                setValue={(next) => setValue({ ...value, [key]: next })}
                value={fieldValue}
              />
            ))
          )}
        </Flex>
      </RedoCollapseSubsection>
    </Flex>
  );
});

const ArrayEditor = memo(function ArrayEditor({
  label,
  value,
  setValue,
  schema,
  defaultCollapsed,
  highlighted,
  readonly,
  searchString,
}: {
  label: string;
  value: unknown[];
  setValue: (value: unknown) => void;
  schema?: unknown;
  defaultCollapsed: boolean;
  highlighted: boolean;
  readonly?: boolean;
  searchString: string;
}) {
  const itemSchema = jsonSchemaItems(schema);
  const visibleItems = value
    .map((item, index) => ({ index, item }))
    .filter(
      ({ item }) => !searchString || jsonValueMatchesSearch(item, searchString),
    );
  const handleAdd = useHandler(() => {
    const last = value[value.length - 1];
    setValue([...value, structuredClone(last)]);
  });
  return (
    <Flex
      bgColor={highlighted ? "warning-secondary" : undefined}
      borderRadius={highlighted ? "sm" : undefined}
      data-json-search-match={highlighted ? "true" : undefined}
      dir="column"
    >
      <RedoCollapseSubsection
        defaultCollapsed={
          searchString || highlighted ? false : defaultCollapsed
        }
        key={searchString || highlighted ? "searching" : "default"}
        title={`${label} [${visibleItems.length}]`}
      >
        <Flex dir="column" gap="xs" pl="xl" pt="xs">
          {visibleItems.map(({ index, item }) => (
            <Flex align="flex-start" gap="xs" key={index}>
              <Flex grow={1} minW="none">
                <JsonValueEditor
                  label={`${index}`}
                  readonly={readonly}
                  schema={itemSchema}
                  searchString={searchString}
                  setValue={(next) =>
                    setValue(value.map((v, i) => (i === index ? next : v)))
                  }
                  value={item}
                />
              </Flex>
              {!readonly && !searchString && (
                <RedoButton
                  aria-label="Delete"
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => setValue(value.filter((_, i) => i !== index))}
                  size="xs"
                />
              )}
            </Flex>
          ))}
          {!readonly && !searchString && (
            <Flex>
              <RedoButton
                disabled={value.length === 0}
                hierarchy="tertiary"
                IconLeading={PlusIcon}
                onClick={handleAdd}
                size="xs"
                text="Add item"
              />
            </Flex>
          )}
        </Flex>
      </RedoCollapseSubsection>
    </Flex>
  );
});

const PrimitiveRow = memo(function PrimitiveRow({
  label,
  value,
  setValue,
  schema,
  highlighted,
  readonly,
}: {
  label: string;
  value: unknown;
  setValue: (value: unknown) => void;
  schema?: unknown;
  highlighted: boolean;
  readonly?: boolean;
}) {
  return (
    <Flex
      align="center"
      bgColor={highlighted ? "warning-secondary" : undefined}
      borderRadius={highlighted ? "sm" : undefined}
      data-json-search-match={highlighted ? "true" : undefined}
      gap="sm"
      justify="space-between"
      px={highlighted ? "xs" : undefined}
    >
      <Text
        className={styles.primitiveLabel}
        fontSize="xs"
        overflow="hidden"
        textColor="secondary"
        textOverflow="ellipsis"
      >
        {label}
      </Text>
      <Flex grow={1} justify="flex-end" minW="none">
        <PrimitiveInput
          readonly={readonly}
          schema={schema}
          setValue={setValue}
          value={value}
        />
      </Flex>
    </Flex>
  );
});

const PrimitiveInput = memo(function PrimitiveInput({
  value,
  setValue,
  schema,
  readonly,
}: {
  value: unknown;
  setValue: (value: unknown) => void;
  schema?: unknown;
  readonly?: boolean;
}) {
  const allowed = allowedPrimitiveTypes(schema);
  const nullable = allowed.has("null");

  if (
    typeof value === "boolean" ||
    (value === null &&
      allowed.has("boolean") &&
      !allowed.has("string") &&
      !allowed.has("number"))
  ) {
    return (
      <RedoCheckbox
        disabled={readonly}
        setValue={(next) => setValue(next === true)}
        size="sm"
        value={typeof value === "boolean" ? value : "indeterminate"}
      />
    );
  }
  if (
    typeof value === "number" ||
    (value === null && allowed.has("number") && !allowed.has("string"))
  ) {
    return (
      <NumberInput
        nullable={nullable}
        readonly={readonly}
        setValue={setValue}
        value={typeof value === "number" ? value : null}
      />
    );
  }
  if (typeof value === "string" || (value === null && allowed.has("string"))) {
    return (
      <StringInput
        nullable={nullable}
        readonly={readonly}
        setValue={setValue}
        value={typeof value === "string" ? value : null}
      />
    );
  }
  // No value to infer a type from and no (usable) schema: nothing to edit
  // against, so render the value as fixed.
  return (
    <Text fontFamily="monospace" fontSize="xs" textColor="secondary">
      {value === null ? "null" : String(value)}
    </Text>
  );
});

/** Map an empty nullable-string draft back to null. */
const StringInput = memo(function StringInput({
  value,
  setValue,
  nullable,
  readonly,
}: {
  value: string | null;
  setValue: (value: unknown) => void;
  nullable: boolean;
  readonly?: boolean;
}) {
  const handleChange = useHandler((text: string) => {
    setValue(nullable && text === "" ? null : text);
  });
  return (
    <RedoTextInput
      placeholder={nullable ? "null" : undefined}
      setValue={handleChange}
      size="sm"
      state={readonly ? "readonly" : "default"}
      value={value ?? ""}
    />
  );
});

/** Keep numeric drafts intact until they parse as complete values. */
const NumberInput = memo(function NumberInput({
  value,
  setValue,
  nullable,
  readonly,
}: {
  value: number | null;
  setValue: (value: unknown) => void;
  nullable: boolean;
  readonly?: boolean;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const [lastCommitted, setLastCommitted] = useState(value);
  if (value !== lastCommitted) {
    // External change (e.g. reset to sample): adopt it.
    setLastCommitted(value);
    setDraft(value === null ? "" : String(value));
  }
  const handleChange = useHandler((text: string) => {
    setDraft(text);
    if (text.trim() === "") {
      if (nullable) {
        setLastCommitted(null);
        setValue(null);
      }
      return;
    }
    if (Number.isFinite(Number(text))) {
      const parsed = Number(text);
      setLastCommitted(parsed);
      setValue(parsed);
    }
  });
  return (
    <RedoTextInput
      inputMode="decimal"
      placeholder={nullable ? "null" : undefined}
      setValue={handleChange}
      size="sm"
      state={readonly ? "readonly" : "default"}
      value={draft}
    />
  );
});
